import { faLink, faRotate, faTriangleExclamation, faWifi } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Popover } from 'antd';
import { useEffect, useState } from 'react';
import { useConnectionStore } from 'src/context/ConnectionContext';
import { useVesselStore } from 'src/context/VesselContext';
import { useToast } from 'src/app/useToast';
import { RECONNECT_NOTICE_TOAST_ID } from 'src/app/recovery/aolBunkerRecovery';
import { getConnectionStatusPresentation } from './connectionStatusModel';
import type { ConnectionStatusPresentation } from './connectionStatusModel';
import {
    DetailRow,
    Details,
    LastUpdateRow,
    NoticeDot,
    PopoverGlobalStyles,
    PopoverHeader,
    PopoverTitleText,
    ReconnectNoticeBanner,
    ReconnectNoticeDismissButton,
    ReconnectNoticeHeader,
    ReconnectNoticeText,
    ReconnectNoticeTitle,
    ScreenReaderOnly,
    StatusButton,
    StatusLabel,
} from './ConnectionStatus.styles';
import { formatRelativeTime } from './formatRelativeTime';

const statusIcons = {
    wifi: faWifi,
    rotate: faRotate,
    'triangle-exclamation': faTriangleExclamation,
    link: faLink,
};

const ConnectionStatusComponent = () => {
    const { selectedVessel } = useVesselStore();
    const {
        offlineLevel,
        connectionStatus,
        connectedVesselGroup,
        syncState,
        lastTransportConnectedAt,
        lastSignalRActivityAt,
        requestSignalRRecovery,
        reconnectNotice,
        clearReconnectNotice,
    } = useConnectionStore();
    const { dismissToast } = useToast();

    const [isOpen, setIsOpen] = useState(false);
    const [now, setNow] = useState(() => new Date());

    const handleDismissNotice = () => {
        clearReconnectNotice();
        dismissToast(RECONNECT_NOTICE_TOAST_ID);
    };

    const handleOpenChange = (nextOpen: boolean) => {
        if (nextOpen) {
            setNow(new Date());
        }
        setIsOpen(nextOpen);
    };

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        const interval = window.setInterval(() => {
            setNow(new Date());
        }, 15000);
        return () => window.clearInterval(interval);
    }, [isOpen]);

    const presentation: ConnectionStatusPresentation = getConnectionStatusPresentation({
        offlineLevel,
        connectionStatus,
        selectedVesselImoNumber: selectedVessel?.imoNumber,
        connectedVesselGroup,
        syncState,
    });

    const lastUpdateTimestamp = lastSignalRActivityAt ?? lastTransportConnectedAt;
    const relativeTime = formatRelativeTime(lastUpdateTimestamp, now);

    const popoverTitle = (
        <PopoverHeader role="heading" aria-level={2} tone={presentation.tone}>
            <FontAwesomeIcon icon={statusIcons[presentation.icon]} aria-hidden="true" />
            <PopoverTitleText>Connection status</PopoverTitleText>
            <ScreenReaderOnly> - {presentation.label}</ScreenReaderOnly>
        </PopoverHeader>
    );

    const content = (
        <Details>
            <DetailRow>{presentation.detail}</DetailRow>
            {reconnectNotice && (
                <ReconnectNoticeBanner role="status" aria-live="polite">
                    <ReconnectNoticeHeader>
                        <FontAwesomeIcon icon={faRotate} aria-hidden="true" />
                        <ReconnectNoticeTitle>{reconnectNotice.title}</ReconnectNoticeTitle>
                    </ReconnectNoticeHeader>
                    <ReconnectNoticeText>{reconnectNotice.message}</ReconnectNoticeText>
                    <ReconnectNoticeDismissButton
                        type="button"
                        onClick={handleDismissNotice}
                    >
                        Dismiss
                    </ReconnectNoticeDismissButton>
                </ReconnectNoticeBanner>
            )}
            <LastUpdateRow>
                <span>Last update received: </span>
                <time
                    dateTime={lastUpdateTimestamp?.toISOString()}
                    title={lastUpdateTimestamp ? lastUpdateTimestamp.toLocaleString() : undefined}
                    aria-label={`Last update received: ${relativeTime}`}
                >
                    {relativeTime}
                </time>
            </LastUpdateRow>
            {presentation.canRecover && presentation.actionLabel && (
                <Button
                    disabled={!requestSignalRRecovery}
                    onClick={() => void requestSignalRRecovery?.()}
                    type="primary"
                >
                    {presentation.actionLabel}
                </Button>
            )}
        </Details>
    );

    const buttonAriaLabel = reconnectNotice
        ? `Connection status: ${presentation.label}. Notice: ${reconnectNotice.title} ${reconnectNotice.message}. Activate for connection details.`
        : `Connection status: ${presentation.label}. Activate for connection details.`;

    return (
        <>
            <PopoverGlobalStyles />
            <Popover
                content={content}
                open={isOpen}
                onOpenChange={handleOpenChange}
                overlayClassName="connection-status-popover"
                overlayInnerStyle={{ padding: 0, overflow: 'hidden' }}
                title={popoverTitle}
                trigger="click"
            >
                <StatusButton
                    aria-label={buttonAriaLabel}
                    tone={presentation.tone}
                    type="button"
                >
                    <FontAwesomeIcon icon={statusIcons[presentation.icon]} />
                    <StatusLabel aria-atomic="true" aria-live="polite">
                        {presentation.label}
                    </StatusLabel>
                    {reconnectNotice && <NoticeDot aria-hidden="true" />}
                </StatusButton>
            </Popover>
        </>
    );
};

export default ConnectionStatusComponent;
